import { computeListSelectionClick } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/listSelectionClick";
import { useCallback, useMemo, useRef } from "react";
import {
  dispatchCrossScopeSelect,
  useCrossScopeSelectListener,
} from "./canvasRailSelectionEvents";

interface UseCanvasRailSelectionParams {
  selectedNodeIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  selectedFileIds: Set<string>;
  onFileSelectionChange: (ids: Set<string>) => void;
  fileRecordIds: Set<string>;
  fileRecordIdToNodeId: Map<string, string>;
  visibleItemIdOrder: string[];
}

export function useCanvasRailSelection({
  selectedNodeIds,
  onSelectionChange,
  selectedFileIds,
  onFileSelectionChange,
  fileRecordIds,
  fileRecordIdToNodeId,
  visibleItemIdOrder,
}: UseCanvasRailSelectionParams) {
  const lastSelectedItemRef = useRef<string | null>(null);

  const selectedItemIds = useMemo(
    () => new Set<string>([...selectedFileIds, ...selectedNodeIds]),
    [selectedFileIds, selectedNodeIds],
  );

  const writeSelection = useCallback(
    (next: Set<string>) => {
      const nextFiles = new Set<string>();
      const nextNodes = new Set<string>();
      for (const id of next) {
        if (fileRecordIds.has(id)) nextFiles.add(id);
        else nextNodes.add(id);
      }
      onFileSelectionChange(nextFiles);
      onSelectionChange(nextNodes);

      const placedIds: string[] = [...nextNodes];
      for (const id of nextFiles) {
        const nodeId = fileRecordIdToNodeId.get(id);
        if (nodeId) placedIds.push(nodeId);
      }
      window.dispatchEvent(
        new CustomEvent("canvas:select-nodes", {
          detail: { nodeIds: placedIds },
        }),
      );

      if (next.size > 0) dispatchCrossScopeSelect("contents");
    },
    [
      fileRecordIds,
      fileRecordIdToNodeId,
      onFileSelectionChange,
      onSelectionChange,
    ],
  );

  const clearLocalSelections = useCallback(() => {
    onFileSelectionChange(new Set());
    onSelectionChange(new Set());
    lastSelectedItemRef.current = null;
    window.dispatchEvent(
      new CustomEvent("canvas:select-nodes", { detail: { nodeIds: [] } }),
    );
  }, [onFileSelectionChange, onSelectionChange]);

  useCrossScopeSelectListener("contents", clearLocalSelections);

  const handleSelectItem = useCallback(
    (id: string, e: React.MouseEvent) => {
      const { next, anchor } = computeListSelectionClick({
        id,
        e,
        selectedIds: selectedItemIds,
        orderedIds: visibleItemIdOrder,
        anchor: lastSelectedItemRef.current,
      });
      lastSelectedItemRef.current = anchor;
      writeSelection(next);
    },
    [selectedItemIds, visibleItemIdOrder, writeSelection],
  );

  /**
   * Row activation under the rail-wide single-click-opens model: a modifier
   * click (cmd/ctrl toggle, shift range) is a pure multi-select and never
   * opens; a plain click selects just this item — keeping the canvas
   * highlight sync and add-to-context consumers fed — then opens it.
   */
  const handleActivateItem = useCallback(
    (id: string, e: React.MouseEvent, open: () => void) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        handleSelectItem(id, e);
        return;
      }
      lastSelectedItemRef.current = id;
      writeSelection(new Set([id]));
      open();
    },
    [handleSelectItem, writeSelection],
  );

  return {
    selectedItemIds,
    handleSelectItem,
    handleActivateItem,
    selectSingleItem: (id: string) => {
      lastSelectedItemRef.current = id;
      writeSelection(new Set([id]));
    },
    clearLocalSelections,
  };
}
